import React from "react";
import ReactDOM from "react-dom/client";
import { SarvamWidget, SarvamWidgetProps } from "./SarvamWidget";

/**
 * Web Component for Sarvam Widget
 * Usage: <sarvam-widget api-key="..." app-id="..." org-id="..." workspace-id="..." ...></sarvam-widget>
 */
class SarvamWidgetElement extends HTMLElement {
  private root: ReactDOM.Root | null = null;

  // Define observed attributes for reactivity
  static get observedAttributes(): string[] {
    return [
      "api-key",
      "app-id",
      "org-id",
      "workspace-id",
      "user-id",
      "button-text",
      "button-color",
      "button-hover-color",
      "text-color",
      "logo-url",
      "logo-alt",
      "position",
      "size"
    ];
  }

  constructor() {
    super();
    // Create shadow root for style isolation
    const shadowRoot = this.attachShadow({ mode: "open" });

    // Create a container div for React to render into
    const container = document.createElement("div");
    container.id = "sarvam-widget-container";
    shadowRoot.appendChild(container);
  }

  connectedCallback(): void {
    this.renderWidget();
  }

  disconnectedCallback(): void {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    // Re-render when attributes change
    if (this.isConnected && oldValue !== newValue) {
      this.renderWidget();
    }
  }

  private getAttributeValue(name: string): string | undefined {
    const value = this.getAttribute(name);
    return value || undefined;
  }

  private getConfig(): SarvamWidgetProps {
    const config: SarvamWidgetProps = {
      apiKey: this.getAttributeValue("api-key") || "",
      appId: this.getAttributeValue("app-id") || "",
      orgId: this.getAttributeValue("org-id") || "",
      workspaceId: this.getAttributeValue("workspace-id") || "",
      userId: this.getAttributeValue("user-id"),
      buttonText: this.getAttributeValue("button-text"),
      buttonColor: this.getAttributeValue("button-color"),
      buttonHoverColor: this.getAttributeValue("button-hover-color"),
      textColor: this.getAttributeValue("text-color"),
      logoUrl: this.getAttributeValue("logo-url"),
      logoAlt: this.getAttributeValue("logo-alt"),
      position: (this.getAttributeValue("position") as any) || "bottom-right",
      size: (this.getAttributeValue("size") as any) || "medium",
      baseUrl: this.getAttributeValue("base-url"),
      proxy: this.getAttributeValue("proxy") === "true",
    };

    return config;
  }

  private renderWidget(): void {
    const config = this.getConfig();

    // Validate required attributes
    if (
      !config.apiKey ||
      !config.appId ||
      !config.orgId ||
      !config.workspaceId
    ) {
      console.error(
        "Sarvam Widget: Missing required attributes (api-key, app-id, org-id, workspace-id)"
      );
      return;
    }

    const container = this.shadowRoot?.getElementById(
      "sarvam-widget-container"
    );
    if (!container) {
      console.error("Sarvam Widget: Container not found");
      return;
    }

    // Unmount previous instance if exists
    if (this.root) {
      this.root.unmount();
    }

    // Create new React root and render
    this.root = ReactDOM.createRoot(container);
    this.root.render(React.createElement(SarvamWidget, config));
  }

  // Getters and setters for programmatic access
  get apiKey(): string | null {
    return this.getAttribute("api-key");
  }

  set apiKey(value: string | null) {
    if (value) {
      this.setAttribute("api-key", value);
    } else {
      this.removeAttribute("api-key");
    }
  }

  get appId(): string | null {
    return this.getAttribute("app-id");
  }

  set appId(value: string | null) {
    if (value) {
      this.setAttribute("app-id", value);
    } else {
      this.removeAttribute("app-id");
    }
  }

  get orgId(): string | null {
    return this.getAttribute("org-id");
  }

  set orgId(value: string | null) {
    if (value) {
      this.setAttribute("org-id", value);
    } else {
      this.removeAttribute("org-id");
    }
  }

  get workspaceId(): string | null {
    return this.getAttribute("workspace-id");
  }

  set workspaceId(value: string | null) {
    if (value) {
      this.setAttribute("workspace-id", value);
    } else {
      this.removeAttribute("workspace-id");
    }
  }

  get userId(): string | null {
    return this.getAttribute("user-id");
  }

  set userId(value: string | null) {
    if (value) {
      this.setAttribute("user-id", value);
    } else {
      this.removeAttribute("user-id");
    }
  }
}

// Register the custom element
if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  if (!customElements.get("sarvam-widget")) {
    customElements.define("sarvam-widget", SarvamWidgetElement);
  }
}

/**
 * Initialize and render the Sarvam Widget (legacy support)
 * This function is called when the script is loaded
 */
export function initSarvamWidget(
  containerId: string,
  config: SarvamWidgetProps
): void {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error(`Container with id "${containerId}" not found`);
    return;
  }

  const root = ReactDOM.createRoot(container);
  root.render(React.createElement(SarvamWidget, config));
}

// Export for manual initialization
if (typeof window !== "undefined") {
  (window as any).SarvamWidget = {
    init: initSarvamWidget,
  };
}
